import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import API from "../API/newIndex";

export const UserContext = createContext({
  userInfo: null,
  setUserInfo: () => { },
  fetchUserInfo: () => { },
  isUserLoaded: false,
  sortData: {},
  tableData: {},
  updateTableSort: () => { },
  updateTableColumns: () => { },
});

export const UserProvider = ({ children, tokenReady }) => {
  const [userInfo, setUserInfo] = useState(null);
  const isFetching = useRef(false);

  const fetchUserInfo = useCallback(async (force = false) => {
    if (!force && userInfo) {
      return userInfo;
    }
    if (isFetching.current) return;

    isFetching.current = true;
    try {
      const res = await API({ url: "/admin/user/userInfo", method: "get" });
      const data = res?.data?.data || res?.data || {};
      setUserInfo(data);
      return data;
    } catch (error) {
      console.error("Failed to fetch user info:", error);
    } finally {
      isFetching.current = false;
    }
  }, [userInfo]);

  const updateTableSort = useCallback(async (tableId, sortPayload) => {
    if (!tableId || !sortPayload) return;

    // Optimistically update local context state
    setUserInfo((prev) => {
      const currentSort = prev?.tables?.sort_data || {};
      const nextSortData = {
        ...currentSort,
        [tableId]: sortPayload,
      };

      return {
        ...(prev || {}),
        tables: {
          ...(prev?.tables || {}),
          sort_data: nextSortData,
        },
      };
    });

    try {
      await API({
        url: "/admin/user/UpdateTablesData",
        method: "put",
        data: {
          sort_data: {
            [tableId]: sortPayload,
          },
        },
      });
    } catch (error) {
      console.error("Failed to update table sort on server:", error);
    }
  }, []);

  const updateTableColumns = useCallback(async (tableId, visibleColumnsArray) => {
    if (!tableId) return;

    setUserInfo((prev) => {
      const currentTables = prev?.tables || {};
      return {
        ...(prev || {}),
        tables: {
          ...currentTables,
          [tableId]: visibleColumnsArray,
        },
      };
    });

    try {
      await API({
        url: "/admin/user/UpdateTablesData",
        method: "put",
        data: {
          tables: {
            [tableId]: visibleColumnsArray,
          },
        },
      });
    } catch (error) {
      console.error("Failed to update table columns on server:", error);
    }
  }, []);

  useEffect(() => {
    const checkAndFetch = () => {
      const token = localStorage.getItem("token");
      if (token) {
        fetchUserInfo();
      }
    };
    checkAndFetch();
    window.addEventListener("message", checkAndFetch);
    window.addEventListener("storage", checkAndFetch);
    return () => {
      window.removeEventListener("message", checkAndFetch);
      window.removeEventListener("storage", checkAndFetch);
    };
  }, [fetchUserInfo, tokenReady]);

  const sortData = userInfo?.tables?.sort_data || {};
  const tableData = userInfo?.tables || {};

  const isUserLoaded = Boolean(userInfo);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        setUserInfo,
        fetchUserInfo,
        isUserLoaded,
        sortData,
        tableData,
        updateTableSort,
        updateTableColumns,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserContext;
